import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ChevronDown, Search, Calendar, Download, Maximize2, X, User } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { actionBadgeClass } from '../../constants/actionColors';
import { Card, Select, Input, Button, Modal, Tabs, Pagination, LoadingState, EmptyState, Table, THead, TBody, TR, TH, TD } from '../../components/ui';

const ActionBadge = ({ action }) => (
  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${actionBadgeClass(action, 'light')}`}>
    {action}
  </span>
);

const StatusCodeBadge = ({ code }) => {
  const color = code >= 500 ? 'bg-red-100 text-red-700' : code >= 400 ? 'bg-orange-100 text-orange-700' : 'bg-green-100 text-green-700';
  return <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-mono font-medium ${color}`}>{code}</span>;
};

const formatDate = (d) => d ? new Date(d).toLocaleString() : '—';

// Compact "label: value" detail chip, used to lay out the expanded row's
// request metadata as a flowing inline row rather than a blocky grid.
const DetailChip = ({ label, value }) => (
  <span className="text-slate-600">
    <span className="text-slate-400">{label}:</span> {value || '—'}
  </span>
);

const LogRow = ({ log, expanded, onToggle }) => (
  <>
    <TR className="cursor-pointer" onClick={onToggle}>
      <TD className="text-xs whitespace-nowrap">{formatDate(log.createdAt)}</TD>
      <TD><ActionBadge action={log.action} /></TD>
      <TD className="font-medium text-slate-800">{log.entityName || '—'}</TD>
      <TD>
        <div className="flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
            <User className="w-3.5 h-3.5 text-slate-400" />
          </span>
          <div>
            <p className="text-slate-700">{log.userName}</p>
            <p className="text-xs text-slate-400">{log.userRole}</p>
          </div>
        </div>
      </TD>
      <TD className="max-w-xs truncate">{log.description}</TD>
      <TD><StatusCodeBadge code={log.statusCode} /></TD>
      <TD className="text-slate-400">
        <ChevronDown className={`w-4 h-4 transition-transform ${expanded ? 'rotate-180' : ''}`} />
      </TD>
    </TR>
    {expanded && (
      <TR className="bg-slate-50 hover:bg-slate-50">
        <TD colSpan={7}>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 text-xs py-1">
            <DetailChip label="Route" value={`${log.method} ${log.route}`} />
            <DetailChip label="IP" value={log.ipAddress} />
            <DetailChip label="User" value={log.userEmail} />
            {log.entityId && <DetailChip label="Entity ID" value={<span className="font-mono">{log.entityId}</span>} />}
          </div>
          {(log.dataSnapshot || log.changes || log.metadata) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs mt-3">
              {log.dataSnapshot && (
                <div>
                  <p className="font-semibold text-slate-600 mb-1">Snapshot (before deletion)</p>
                  <pre className="bg-white border border-slate-200 rounded p-2 text-xs overflow-auto max-h-40 text-slate-700">
                    {JSON.stringify(log.dataSnapshot, null, 2)}
                  </pre>
                </div>
              )}
              {log.changes && (
                <div>
                  <p className="font-semibold text-slate-600 mb-1">Changes</p>
                  <pre className="bg-white border border-slate-200 rounded p-2 text-xs overflow-auto max-h-40 text-slate-700">
                    {JSON.stringify(log.changes, null, 2)}
                  </pre>
                </div>
              )}
              {log.metadata && (
                <div>
                  <p className="font-semibold text-slate-600 mb-1">Metadata</p>
                  <pre className="bg-white border border-slate-200 rounded p-2 text-xs overflow-auto max-h-40 text-slate-700">
                    {JSON.stringify(log.metadata, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          )}
        </TD>
      </TR>
    )}
  </>
);

const LogTable = ({ logs, loading }) => {
  const [expandedId, setExpandedId] = useState(null);
  const toggle = (id) => setExpandedId(p => p === id ? null : id);

  if (loading) return <LoadingState label="Loading logs..." />;
  if (!logs.length) return <EmptyState title="No logs found" />;

  return (
    <Table>
      <THead>
        <TR>
          <TH>Time</TH>
          <TH>Action</TH>
          <TH>Exam</TH>
          <TH>By</TH>
          <TH>Description</TH>
          <TH>Status</TH>
          <TH></TH>
        </TR>
      </THead>
      <TBody>
        {logs.map(log => (
          <LogRow key={log._id} log={log} expanded={expandedId === log._id} onToggle={() => toggle(log._id)} />
        ))}
      </TBody>
    </Table>
  );
};

// Formats a {from, to} range for the Date Filter button's label.
const formatRangeLabel = (range) => {
  if (!range.from && !range.to) return null;
  const fmt = (d) => new Date(`${d}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  if (range.from && range.to) return `${fmt(range.from)} - ${fmt(range.to)}`;
  return fmt(range.from || range.to);
};

// Date-range popover, positioned with `fixed` (anchored to the trigger
// button's own viewport rect) rather than `absolute` -- an `overflow-y-auto`
// ancestor (AppShell's <main>) also clips the other axis per the CSS
// overflow spec, so `absolute` here gets cut off at the content pane's edge.
// See the equivalent fix in ExamManagement.jsx's Date Filter.
const DateRangeFilter = ({ range, onApply }) => {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(range);
  const [pos, setPos] = useState(null);
  const wrapperRef = useRef(null);
  const btnRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const toggleOpen = () => {
    if (btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + 8, right: Math.max(8, window.innerWidth - rect.right) });
    }
    setDraft(range);
    setOpen((v) => !v);
  };

  const apply = () => { onApply(draft); setOpen(false); };
  const clear = () => { onApply({ from: '', to: '' }); setDraft({ from: '', to: '' }); setOpen(false); };

  const label = formatRangeLabel(range);

  return (
    <div className="relative" ref={wrapperRef}>
      <Button ref={btnRef} variant="secondary" icon={Calendar} onClick={toggleOpen} className={label ? '!border-themePrimary !text-themePrimary' : ''}>
        {label || 'Date Filter'}
      </Button>
      {open && pos && (
        <div className="fixed w-72 bg-white rounded-xl shadow-lg border border-slate-200 p-4 z-50" style={{ top: pos.top, right: pos.right }}>
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm font-semibold text-slate-800">Filter by date</p>
            <button type="button" onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-600">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">From</label>
              <input
                type="date" value={draft.from} onChange={(e) => setDraft((p) => ({ ...p, from: e.target.value }))}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-themePrimary/30 focus:border-themePrimary"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">To</label>
              <input
                type="date" value={draft.to} onChange={(e) => setDraft((p) => ({ ...p, to: e.target.value }))}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-themePrimary/30 focus:border-themePrimary"
              />
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <Button variant="primary" size="sm" fullWidth onClick={apply}>Apply</Button>
            <Button variant="outline" size="sm" onClick={clear}>Clear</Button>
          </div>
        </div>
      )}
    </div>
  );
};

const EXAM_ACTIONS = ['', 'CREATE', 'UPDATE', 'DELETE', 'CLONE', 'ASSIGN'];
const ERROR_ENTITIES = ['', 'Exam', 'User', 'Question', 'ExamAttempt', 'Violation', 'Auth'];

const TABS = [
  { key: 'exam', label: 'Exam Activity' },
  { key: 'error', label: 'Error Logs' },
];

const ExamLogs = () => {
  const [tab, setTab] = useState('exam');

  const [examLogs, setExamLogs] = useState([]);
  const [examLoading, setExamLoading] = useState(true);
  const [examPage, setExamPage] = useState(1);
  const [examPages, setExamPages] = useState(1);
  const [examAction, setExamAction] = useState('');
  const [examSearch, setExamSearch] = useState('');
  const [examDateRange, setExamDateRange] = useState({ from: '', to: '' });
  const [examMaximized, setExamMaximized] = useState(false);

  const [errorLogs, setErrorLogs] = useState([]);
  const [errorLoading, setErrorLoading] = useState(true);
  const [errorPage, setErrorPage] = useState(1);
  const [errorPages, setErrorPages] = useState(1);
  const [errorEntity, setErrorEntity] = useState('');
  const [errorSearch, setErrorSearch] = useState('');
  const [errorDateRange, setErrorDateRange] = useState({ from: '', to: '' });
  const [errorMaximized, setErrorMaximized] = useState(false);

  const fetchExamLogs = useCallback(async () => {
    setExamLoading(true);
    try {
      const params = new URLSearchParams({ page: examPage, limit: 20 });
      if (examAction) params.append('action', examAction);
      if (examSearch) params.append('search', examSearch);
      if (examDateRange.from) params.append('startDate', examDateRange.from);
      if (examDateRange.to) params.append('endDate', examDateRange.to);
      const res = await api.get(`/admin/exam-logs?${params}`);
      const d = res.data.data;
      setExamLogs(d.logs || []);
      setExamPages(d.pages || 1);
    } catch {
      toast.error('Failed to load exam logs');
    } finally {
      setExamLoading(false);
    }
  }, [examPage, examAction, examSearch, examDateRange]);

  const fetchErrorLogs = useCallback(async () => {
    setErrorLoading(true);
    try {
      const params = new URLSearchParams({ page: errorPage, limit: 20 });
      if (errorEntity) params.append('entity', errorEntity);
      if (errorSearch) params.append('search', errorSearch);
      if (errorDateRange.from) params.append('startDate', errorDateRange.from);
      if (errorDateRange.to) params.append('endDate', errorDateRange.to);
      const res = await api.get(`/admin/error-logs?${params}`);
      const d = res.data.data;
      setErrorLogs(d.logs || []);
      setErrorPages(d.pages || 1);
    } catch {
      toast.error('Failed to load error logs');
    } finally {
      setErrorLoading(false);
    }
  }, [errorPage, errorEntity, errorSearch, errorDateRange]);

  useEffect(() => { fetchExamLogs(); }, [fetchExamLogs]);
  useEffect(() => { fetchErrorLogs(); }, [fetchErrorLogs]);

  const downloadCsv = async (endpoint, params, filenameFallback) => {
    try {
      const res = await api.get(endpoint, { params, responseType: 'blob' });
      const disposition = res.headers['content-disposition'] || '';
      const match = disposition.match(/filename="(.+)"/);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = match ? match[1] : filenameFallback;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Failed to export CSV');
    }
  };

  const handleExportExamLogs = () => downloadCsv('/admin/exam-logs/export', {
    action: examAction || undefined, search: examSearch || undefined,
    startDate: examDateRange.from || undefined, endDate: examDateRange.to || undefined,
  }, 'exam_audit_trail.csv');

  const handleExportErrorLogs = () => downloadCsv('/admin/error-logs/export', {
    entity: errorEntity || undefined, search: errorSearch || undefined,
    startDate: errorDateRange.from || undefined, endDate: errorDateRange.to || undefined,
  }, 'error_logs.csv');

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-slate-900">Logs</h2>
        <p className="text-slate-500 text-sm mt-1">Audit trail for exam operations and system errors</p>
      </div>

      <Tabs tabs={TABS} active={tab} onChange={setTab} />

      {tab === 'exam' && (
        <Card padding="p-0">
          <div className="flex flex-col lg:flex-row lg:items-center gap-3 px-4 py-3 border-b border-slate-200">
            <p className="text-sm font-semibold text-slate-700 lg:mr-1">Exam Audit Trail</p>
            <div className="flex-1 min-w-[180px]">
              <Input leftIcon={Search} placeholder="Search by exam, user, or IP..." value={examSearch} onChange={(e) => { setExamSearch(e.target.value); setExamPage(1); }} />
            </div>
            <Select value={examAction} onChange={e => { setExamAction(e.target.value); setExamPage(1); }} className="lg:w-44">
              {EXAM_ACTIONS.map(a => <option key={a} value={a}>{a || 'All Actions'}</option>)}
            </Select>
            <DateRangeFilter range={examDateRange} onApply={(r) => { setExamDateRange(r); setExamPage(1); }} />
            <Button variant="secondary" icon={Download} onClick={handleExportExamLogs}>Export CSV</Button>
            <button type="button" onClick={() => setExamMaximized(true)} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg flex-shrink-0" title="Expand">
              <Maximize2 className="w-4 h-4" />
            </button>
          </div>
          <LogTable logs={examLogs} loading={examLoading} />
          {examPages > 1 && (
            <div className="flex justify-end px-4 py-3 border-t border-slate-200">
              <Pagination page={examPage} totalPages={examPages} onChange={setExamPage} />
            </div>
          )}
        </Card>
      )}

      {tab === 'error' && (
        <Card padding="p-0">
          <div className="flex flex-col lg:flex-row lg:items-center gap-3 px-4 py-3 border-b border-slate-200">
            <p className="text-sm font-semibold text-slate-700 lg:mr-1">System Error Logs</p>
            <div className="flex-1 min-w-[180px]">
              <Input leftIcon={Search} placeholder="Search by entity, user, or IP..." value={errorSearch} onChange={(e) => { setErrorSearch(e.target.value); setErrorPage(1); }} />
            </div>
            <Select value={errorEntity} onChange={e => { setErrorEntity(e.target.value); setErrorPage(1); }} className="lg:w-44">
              {ERROR_ENTITIES.map(e => <option key={e} value={e}>{e || 'All Entities'}</option>)}
            </Select>
            <DateRangeFilter range={errorDateRange} onApply={(r) => { setErrorDateRange(r); setErrorPage(1); }} />
            <Button variant="secondary" icon={Download} onClick={handleExportErrorLogs}>Export CSV</Button>
            <button type="button" onClick={() => setErrorMaximized(true)} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg flex-shrink-0" title="Expand">
              <Maximize2 className="w-4 h-4" />
            </button>
          </div>
          <LogTable logs={errorLogs} loading={errorLoading} />
          {errorPages > 1 && (
            <div className="flex justify-end px-4 py-3 border-t border-slate-200">
              <Pagination page={errorPage} totalPages={errorPages} onChange={setErrorPage} />
            </div>
          )}
        </Card>
      )}

      <Modal open={examMaximized} onClose={() => setExamMaximized(false)} title="Exam Audit Trail" size="xl">
        <LogTable logs={examLogs} loading={examLoading} />
        {examPages > 1 && (
          <div className="flex justify-end pt-3">
            <Pagination page={examPage} totalPages={examPages} onChange={setExamPage} />
          </div>
        )}
      </Modal>

      <Modal open={errorMaximized} onClose={() => setErrorMaximized(false)} title="System Error Logs" size="xl">
        <LogTable logs={errorLogs} loading={errorLoading} />
        {errorPages > 1 && (
          <div className="flex justify-end pt-3">
            <Pagination page={errorPage} totalPages={errorPages} onChange={setErrorPage} />
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ExamLogs;
